/**
 * Replaces template variables in text with actual values from template configuration
 * Supports single and double percentage signs (e.g., %lenderName% or %%lenderName%%)
 *
 * @param text - The text containing template variables
 * @param templateConfig - The template configuration object
 * @returns The text with all variables replaced
 */

interface TemplateConfig {
  lenderName?: string;
  nmlsNumber?: string;
  phoneContact?: string;
  companyInfo?: {
    url?: string;
    name?: string;
    alias?: string;
  };
  lenderAddress?: string;
  lenderZip?: string;
  lenderCity?: string;
  lenderState?: string;
  lenderCountry?: string;
  programType?: string;
  companyUrl?: string;
  minimumCreditScore?: number;
  [key: string]: any;
}

export const replaceTemplateVariables = (
  text: string | undefined | null,
  templateConfig: TemplateConfig | null | undefined,
  additionalVariables?: Record<string, string | number>,
): string => {
  if (!text || typeof text !== 'string') return text || '';
  if (!templateConfig) return text;

  const currentYear = new Date().getFullYear();

  // Build formatted lender address from individual components
  const buildLenderAddress = (): string => {
    const addressParts: string[] = [];

    // Add street address if available
    if (templateConfig?.lenderAddress) {
      addressParts.push(templateConfig.lenderAddress);
    }

    // Build city, state zip line
    const cityStateZip: string[] = [];
    if (templateConfig?.lenderCity) {
      cityStateZip.push(templateConfig.lenderCity);
    }
    if (templateConfig?.lenderState) {
      cityStateZip.push(templateConfig.lenderState);
    }
    if (templateConfig?.lenderZip) {
      cityStateZip.push(templateConfig.lenderZip);
    }

    if (cityStateZip.length > 0) {
      addressParts.push(cityStateZip.join(' '));
    }

    // Add country if available and not United States (to avoid redundancy)
    if (templateConfig?.lenderCountry && templateConfig.lenderCountry !== 'United States') {
      addressParts.push(templateConfig.lenderCountry);
    }

    // If we have individual components, use them; otherwise fall back to lenderAddress string
    if (addressParts.length > 0) {
      return addressParts.join(', ');
    }

    // Fallback to single lenderAddress string if individual components not available
    return templateConfig?.lenderAddress || '';
  };

  const formattedLenderAddress = buildLenderAddress();
  const companyUrl = templateConfig?.companyInfo?.url || templateConfig?.companyUrl || templateConfig?.pages?.returnPage || '';

  // Build replacements object with all supported variables
  const replacements: Record<string, string> = {
    // Lender information
    '%lenderName%': templateConfig?.lenderName || '',
    '%%lenderName%%': templateConfig?.lenderName || '',
    '%lenderAlias%': templateConfig?.companyInfo?.alias || templateConfig?.companyInfo?.name || templateConfig?.lenderName || '',
    '%%lenderAlias%%': templateConfig?.companyInfo?.alias || templateConfig?.companyInfo?.name || templateConfig?.lenderName || '',
    '%nmlsNumber%': templateConfig?.nmlsNumber || '',
    '%lenderAddress%': formattedLenderAddress,
    '%%lenderAddress%%': formattedLenderAddress,

    // Contact information
    '%phoneContact%': templateConfig?.phoneContact || '',

    // Company URLs
    '%companyUrl%': companyUrl,
    '%companyurl%': companyUrl,
    '%companyName%': templateConfig?.companyInfo?.name || templateConfig?.lenderName || '',

    // Date/Time
    '%currentYear%': currentYear.toString(),

    // Program information
    '%programType%': templateConfig?.programType || '',
    '%minimumCreditScore%': templateConfig?.minimumCreditScore?.toString() || '',

    // Special cases
    // Note: %Read eConsent% is handled by TextBlock component to display as a clickable link
    // Do not remove it here - let TextBlock handle the replacement
  };

  // Add any additional variables passed in
  if (additionalVariables) {
    Object.entries(additionalVariables).forEach(([key, value]) => {
      // Clean the key to remove any existing % characters
      const cleanKey = key.replace(/^%+|%+$/g, '');
      const wrappedKey = `%${cleanKey}%`;
      const doubleWrappedKey = `%%${cleanKey}%%`; // Support both %key% and %%key%%
      const valueStr = value?.toString() || '';
      replacements[wrappedKey] = valueStr;
      replacements[doubleWrappedKey] = valueStr;

      // Support lowercase variants for coApplicantLabel
      if (cleanKey === 'coApplicantLabel') {
        const lowercaseValue = valueStr.toLowerCase();
        replacements['%coApplicantLabelLowercase%'] = lowercaseValue;
        replacements['%%coApplicantLabelLowercase%%'] = lowercaseValue;
      }
    });
  }

  // Perform all replacements - process double %% first to avoid partial matches
  let replacedText = text;
  // Sort keys by length (longest first) to ensure %%key%% is replaced before %key%
  const sortedKeys = Object.keys(replacements).sort((a, b) => b.length - a.length);
  sortedKeys.forEach((key) => {
    // Escape special regex characters in the key
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    replacedText = replacedText.replace(new RegExp(escapedKey, 'g'), replacements[key]);
  });

  return replacedText;
};

/**
 * React hook version for use in components
 */
const _useTemplateVariables = (templateConfig: TemplateConfig | null | undefined) => {
  return (text: string | undefined | null, additionalVariables?: Record<string, string | number>) => {
    return replaceTemplateVariables(text, templateConfig, additionalVariables);
  };
};
